/**
 * Drilldown Modal Component for Supplier Performance
 *
 * The shell only: title, Export, the scroll container and the pager. The
 * columns per type live in ./drilldown (one definition each for the table and
 * the export).
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useFormatters } from '../../contexts/PreferencesContext';
import { AlertCircle, Download, Loader2 } from 'lucide-react';
import { ModalType, POLineMetric } from './types';
import { MODAL_PAGE_SIZE } from './constants';
import { AnyRow, DrilldownFormatters } from './drilldown/columns';
import { columnsFor } from './drilldown/columnsFor';
import { DrilldownTable } from './drilldown/DrilldownTable';

interface DrilldownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  type: ModalType;
  /** The cell label the modal opened from ('Reject Value' widens the NCR columns). */
  filterType?: string;
  /** PO-line drills: the cell's population ('qty_diff' adds a Qty Diff column). */
  metric?: POLineMetric;
  /** Supplier viewer: the customer's reporting-currency columns are withheld. */
  isSupplier?: boolean;
  data: unknown[];
  loading: boolean;
  error?: string | null;
  page: number;
  totalCount: number;
  totalPages: number;
  currency: string;
  onPageChange: (page: number) => void;
  /** Exports the FULL filtered population (every page) with the table's columns. */
  onExport?: () => void;
  exporting?: boolean;
}

export function DrilldownModal({
  open,
  onOpenChange,
  title,
  subtitle,
  type,
  filterType,
  metric,
  isSupplier = false,
  data,
  loading,
  error,
  page,
  totalCount,
  totalPages,
  currency,
  onPageChange,
  onExport,
  exporting = false,
}: DrilldownModalProps) {
  const { formatDate, formatDateTime, formatCurrency } = useFormatters();
  const formatters: DrilldownFormatters = {
    formatDate,
    formatDateTime,
    money: (value, code) => formatCurrency(value, code, 2),
    currency,
  };
  const rows = data as AnyRow[];
  const columns = columnsFor(type, formatters, { filterType, metric, isSupplier });
  const total = totalCount || data.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* pr-8 keeps the Export button clear of the dialog's absolute close button. */}
        <DialogHeader className="pr-8">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{subtitle}</DialogDescription>
            </div>
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExport}
                disabled={exporting || loading || total === 0}
                title="Export every record in this drill-down to Excel"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* min-h prevents layout shift during loading that causes visual jerking.
            scrollbar-always: the wide tables scroll sideways here, and a mouse
            user needs the bar drawn to reach the right-hand columns. */}
        <div className="flex-1 min-h-[400px] overflow-y-auto scrollbar-always">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center gap-2 py-8 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No records behind this figure for the selected filters.
            </p>
          ) : (
            <DrilldownTable rows={rows} columns={columns} formatters={formatters} />
          )}
        </div>

        {/* Pagination footer */}
        {!loading && !error && rows.length > 0 && (
          <div className="pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Showing {((page - 1) * MODAL_PAGE_SIZE + 1).toLocaleString()} -{' '}
              {Math.min(page * MODAL_PAGE_SIZE, total).toLocaleString()} of{' '}
              {total.toLocaleString()} record{total !== 1 ? 's' : ''}
            </div>
            {(totalCount > MODAL_PAGE_SIZE || rows.length === MODAL_PAGE_SIZE) && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="px-2">
                  Page {page} of {totalPages || '?'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
